import { useEffect, useState } from "react";
import axios from "axios";
import { Plane, AlertCircle, Check, Clock, ChevronDown, ChevronUp, MapPin } from "lucide-react";

const STATUS_STYLE = {
  active: { bg: "bg-blue-100", text: "text-blue-800", icon: <Plane className="w-3 h-3" />, label: "In Flight" },
  landed: { bg: "bg-emerald-100", text: "text-emerald-800", icon: <Check className="w-3 h-3" />, label: "Landed" },
  scheduled: { bg: "bg-slate-100", text: "text-slate-700", icon: <Clock className="w-3 h-3" />, label: "Scheduled" },
  delayed: { bg: "bg-amber-100", text: "text-amber-900", icon: <AlertCircle className="w-3 h-3" />, label: "Delayed" },
  cancelled: { bg: "bg-rose-100", text: "text-rose-800", icon: <AlertCircle className="w-3 h-3" />, label: "Cancelled" },
  diverted: { bg: "bg-purple-100", text: "text-purple-800", icon: <AlertCircle className="w-3 h-3" />, label: "Diverted" },
  unknown: { bg: "bg-gray-100", text: "text-gray-600", icon: <Clock className="w-3 h-3" />, label: "Status N/A" },
};

const apiBase = () => `${window.location.origin}/api`;

const cleanTime = (t) => (typeof t === "string" ? t.replace("T", " ").slice(0, 16) : "—");

export default function FlightStatusBadge({ flightNumber, travelDate, terminal }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!flightNumber) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    const load = async () => {
      try {
        const res = await axios.get(
          `${apiBase()}/flight/lookup/${encodeURIComponent(flightNumber.trim().toUpperCase())}${travelDate ? `?date=${travelDate}` : ""}`,
          { timeout: 8000 }
        );
        if (!cancelled) setData(res.data);
      } catch {
        if (!cancelled) setData({ found: false });
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [flightNumber, travelDate]);

  if (!flightNumber) return null;
  if (loading) {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-slate-100 text-slate-500 text-[10px] font-semibold">
        <Clock className="w-3 h-3 animate-pulse" /> …
      </span>
    );
  }
  if (!data || !data.found) {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-gray-100 text-gray-500 text-[10px] font-semibold">
        <Plane className="w-3 h-3" /> {flightNumber}
      </span>
    );
  }

  const isDelayed = (data.delay_minutes || 0) > 5;
  const flightStatus = (data.flight_status || "scheduled").toLowerCase();
  const styleKey = isDelayed && flightStatus !== "landed" && flightStatus !== "cancelled" ? "delayed" : (STATUS_STYLE[flightStatus] ? flightStatus : "unknown");
  const style = STATUS_STYLE[styleKey];
  const arr = data.arrival || {};
  const dep = data.departure || {};

  return (
    <div className="inline-flex flex-col" data-testid={`flight-status-${flightNumber}`}>
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); setOpen((v) => !v); }}
        className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold ${style.bg} ${style.text} hover:opacity-90`}
        title="Tap for full flight details"
      >
        {style.icon}
        <span>{style.label}</span>
        {isDelayed && (
          <span className="font-bold ml-0.5">+{data.delay_minutes}m</span>
        )}
        {open ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
      </button>

      {open && (
        <div className="mt-2 p-2 bg-slate-50 border border-slate-200 rounded text-[11px] text-slate-700 space-y-1 w-full sm:w-72 z-10">
          <div className="flex items-center gap-1 font-bold text-slate-800">
            <Plane className="w-3 h-3" /> {data.airline} {data.flight_number}
          </div>

          {dep.airport && (
            <div>
              <span className="text-slate-500">Departing:</span>{" "}
              <span className="font-medium">{dep.airport}{dep.iata ? ` (${dep.iata})` : ""}</span>
              {dep.scheduled && <span className="text-slate-500"> · {cleanTime(dep.scheduled)}</span>}
            </div>
          )}

          {arr.airport && (
            <div>
              <span className="text-slate-500">Arriving:</span>{" "}
              <span className="font-medium">{arr.airport}{arr.iata ? ` (${arr.iata})` : ""}</span>
            </div>
          )}

          {(arr.scheduled || data.arrival_time_prefill) && (
            <div>
              <span className="text-slate-500">Scheduled arrival:</span>{" "}
              <span className="font-mono">{cleanTime(arr.scheduled) || data.arrival_time_prefill}</span>
            </div>
          )}

          {arr.estimated && arr.estimated !== arr.scheduled && (
            <div>
              <span className="text-slate-500">Estimated arrival:</span>{" "}
              <span className={`font-mono ${isDelayed ? "text-amber-700 font-bold" : ""}`}>
                {cleanTime(arr.estimated)}
              </span>
            </div>
          )}

          {(terminal || arr.terminal) && (
            <div className="flex items-center gap-1">
              <MapPin className="w-3 h-3 text-slate-400" />
              <span className="text-slate-500">Terminal:</span>{" "}
              <span className="font-bold">{terminal || arr.terminal}</span>
              {arr.gate && <span className="text-slate-500"> · Gate {arr.gate}</span>}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
